import {
  BookOpen,
  Bot,
  Building2,
  CreditCard,
  GitBranch,
  Inbox,
  type LucideIcon,
  Megaphone,
  MessageCircleQuestion,
  SendHorizontal,
  Users,
} from "lucide-react";

export interface SettingsLink {
  path: string;
  label: string;
  icon: LucideIcon;
  /** Hidden from members — the settings behind these are workspace-owner only. */
  ownerOnly?: boolean;
}

export interface SettingsGroup {
  title: string;
  links: SettingsLink[];
}

export const settingsGroups: SettingsGroup[] = [
  {
    title: "Workspace",
    links: [
      { path: "general", label: "General", icon: Building2 },
      { path: "workflow", label: "Workflow", icon: GitBranch },
      { path: "members", label: "Members", icon: Users },
      { path: "inbox", label: "Inbox", icon: Inbox },
    ],
  },
  {
    title: "Portals",
    links: [
      { path: "applications", label: "Applications", icon: SendHorizontal },
      { path: "portco-updates", label: "Portco Updates", icon: Building2 },
      {
        path: "raise-board",
        label: "Raiseboard",
        icon: Megaphone,
        ownerOnly: true,
      },
    ],
  },
  {
    title: "AI",
    links: [
      { path: "thesis", label: "Thesis", icon: BookOpen },
      { path: "analysis", label: "Agents", icon: Bot },
      { path: "ai-deal-questions", label: "AI Deal Questions", icon: MessageCircleQuestion },
    ],
  },
  {
    title: "Organization",
    links: [
      { path: "billing", label: "Billing", icon: CreditCard },
    ],
  },
];

// What a member should actually see. Two cuts, both for the same reason — the
// BE 403s them, so surfacing the link only routes them to a dead page:
// org-scoped settings (billing) and the owner-only workspace settings.
export function visibleSettingsGroups(isOwner: boolean): SettingsGroup[] {
  if (isOwner) return settingsGroups;
  return settingsGroups
    .filter((group) => group.title !== "Organization")
    .map((group) => ({
      ...group,
      links: group.links.filter((link) => !link.ownerOnly),
    }))
    .filter((group) => group.links.length > 0);
}

// Billing lives under /org/:orgId/settings/billing (org-scoped). Every other
// link lives under /:workspaceId/settings/:path. Callers pass the active
// workspaceId + the user's company id (from /me) so the billing entry can
// resolve to the org-scoped path while remaining grouped with the rest of
// the settings nav.
export function resolveSettingsHref(
  path: string,
  workspaceId: string | undefined,
  companyId: string | undefined
): string {
  if (path === "billing" && companyId) {
    return `/org/${companyId}/settings/billing`;
  }
  return `/${workspaceId}/settings/${path}`;
}
